import {
    Box,
    Typography,
    Tooltip,
    IconButton,
    Divider,
    CircularProgress,
} from "@mui/material";
import { Goal } from "../../../../../utils/goalsApi";
import { ExpandMore, ExpandLess, Flag, Edit, Delete, ChevronLeft, ChevronRight } from "@mui/icons-material";
import { historyCard } from "../../style";
import { DateTime } from "luxon";
import { useMemo } from "react";

interface GoalCardProps {
    goal: Goal;
    onEdit?: (goal: Goal) => void;
    onDelete?: (goalId: string) => void;
    onExpand: (goalId: string) => void;
    isExpanded: boolean;
    progressData: Array<{ date: DateTime; status: string; day: number }>;
    isLoadingProgress: boolean;
    month: string;
    onMonthChange: (month: string) => void;
    minDate: DateTime | null;
    maxDate: DateTime | null;
}

const getStatusLabel = (status: string): string => {
    switch (status) {
        case 'not_started':
            return 'Not Started';
        case 'in_progress':
            return 'In Progress';
        case 'completed':
            return 'Completed';
        default:
            return 'Not Started';
    }
};

// Get color for chart visualization - GitHub-style green palette
const getStatusChartColor = (status: string): string => {
    switch (status) {
        case 'not_started':
            return '#ebedf0'; // Very light grey (GitHub's no contribution color)
        case 'in_progress':
            return '#ffd89b'; // Light orange
        case 'completed':
            return '#40c463'; // Medium green (GitHub's level 2)
        default:
            return '#ebedf0';
    }
};

const GoalCard = ({
    goal,
    onEdit,
    onDelete,
    onExpand,
    isExpanded,
    progressData,
    isLoadingProgress,
    month,
    onMonthChange,
    minDate,
    maxDate,
}: GoalCardProps) => {
    const selectedDate = useMemo(() =>
        DateTime.fromFormat(month, 'yyyy-MM'),
        [month]
    );

    const canGoPrevious = useMemo(() =>
        minDate ? selectedDate > minDate : false,
        [selectedDate, minDate]
    );

    const canGoNext = useMemo(() =>
        maxDate ? selectedDate < maxDate : false,
        [selectedDate, maxDate]
    );

    const getDisplayTitle = (): string => {
        if (!selectedDate) return 'Progress';
        const now = DateTime.now();

        const isCurrentMonth = selectedDate.hasSame(now, 'month') && selectedDate.hasSame(now, 'year');
        if (isCurrentMonth) return 'This Month Progress';

        const lastMonth = now.minus({ months: 1 });
        const isLastMonth = selectedDate.hasSame(lastMonth, 'month') && selectedDate.hasSame(lastMonth, 'year');
        if (isLastMonth) return 'Last Month Progress';

        return `${selectedDate.toFormat('MMMM yyyy')} Progress`;
    };

    const handlePreviousMonth = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (canGoPrevious && !isLoadingProgress) {
            const newDate = selectedDate.minus({ months: 1 });
            onMonthChange(newDate.toFormat('yyyy-MM'));
        }
    };

    const handleNextMonth = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (canGoNext && !isLoadingProgress) {
            const newDate = selectedDate.plus({ months: 1 });
            onMonthChange(newDate.toFormat('yyyy-MM'));
        }
    };

    const handleCardClick = () => {
        onExpand(goal.id);
    };

    return (
        <Box
            sx={{
                ...historyCard,
                borderRadius: 2,
                overflow: "hidden",
                bgcolor: "white",
                transition: "box-shadow 0.2s ease",
                "&:hover": {
                    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.08), 0 1px 2px rgba(0, 0, 0, 0.06)",
                },
            }}
        >
            {/* Header Section */}
            <Box
                sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    px: 1,
                    // py: 0.75,
                    cursor: "pointer",

                }}
                onClick={handleCardClick}
            >
                {/* Left: Title & Description */}
                <Box sx={{ flex: 1, pr: 1 }}>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 0.75, mb: 0.5 }}>
                        <Flag
                            sx={{
                                color: "primary.main",
                                fontSize: 18,
                            }}
                        />
                        <Typography
                            variant="h6"
                            sx={{
                                fontWeight: 600,
                                fontSize: "1rem",
                                color: "#111827",
                            }}
                        >
                            {goal.title}
                        </Typography>
                    </Box>
                    {(goal.description || goal.createdAt || goal.confidenceScore) && (
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 1,
                                ml: 3,
                                flexWrap: "wrap",
                            }}
                        >
                            {goal.description && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: "#6b7280",
                                        fontSize: "0.813rem",
                                        lineHeight: 1.5,
                                        whiteSpace: "pre-wrap",
                                    }}
                                >
                                    {goal.description}
                                </Typography>
                            )}
                            {goal.description && goal.createdAt && (
                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{
                                        height: 16,
                                        borderColor: "#d1d5db",
                                    }}
                                />
                            )}
                            {goal.createdAt && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "#9ca3af",
                                        fontSize: "0.75rem",
                                    }}
                                >
                                    Assigned on {DateTime.fromISO(goal.createdAt).toFormat('MMM d, yyyy')}
                                </Typography>
                            )}
                            {((goal.description || goal.createdAt) && goal.confidenceScore) && (
                                <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{
                                        height: 16,
                                        borderColor: "#d1d5db",
                                    }}
                                />
                            )}
                            {goal.confidenceScore && (
                                <Typography
                                    variant="caption"
                                    sx={{
                                        color: "#9ca3af",
                                        fontSize: "0.75rem",
                                    }}
                                >
                                    Confidence: {goal.confidenceScore}/100
                                </Typography>
                            )}
                        </Box>
                    )}
                </Box>

                {/* Right: Actions & Expand/Collapse */}
                <Box
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        alignSelf: "center",
                        gap: 0.5,
                        flexShrink: 0,
                    }}
                >
                    {/* Edit & Delete Actions */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.25,
                            mr: 1,
                        }}
                        onClick={(e) => e.stopPropagation()}
                    >
                        {onEdit && (
                            <Tooltip title="Edit goal">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onEdit(goal);
                                    }}
                                    sx={{
                                        color: "#6b7280",
                                        "&:hover": {
                                            color: "primary.main",
                                            bgcolor: "action.hover",
                                        },
                                    }}
                                >
                                    <Edit fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                        {onDelete && (
                            <Tooltip title="Delete goal">
                                <IconButton
                                    size="small"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onDelete(goal.id);
                                    }}
                                    sx={{
                                        color: "#6b7280",
                                        "&:hover": {
                                            color: "error.main",
                                            bgcolor: "action.hover",
                                        },
                                    }}
                                >
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        )}
                    </Box>

                    {/* View Progress */}
                    <Box
                        sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                            color: "primary.main",
                        }}
                    >
                        {
                            !isExpanded && <Typography
                                variant="caption"
                                sx={{
                                    color: "#9ca3af",
                                }}
                            >
                                View progress
                            </Typography>
                        }
                        {isExpanded ? <ExpandLess /> : <ExpandMore />}
                    </Box>
                </Box>
            </Box>

            {/* Progress Heatmap Section (Collapsible) */}
            {isExpanded && (
                <Box
                    sx={{
                        px: 1,
                        py: 0.75,
                        borderTop: 1,
                        borderColor: "divider",
                        bgcolor: "#f9fafb",
                    }}
                >
                    <Box
                        sx={{
                            display: "flex",
                            gap: 2,
                            alignItems: "flex-start",
                            justifyContent: "space-between",
                            pt: 1
                        }}
                    >
                        {/* Left: Heatmap Grid */}
                        <Box>
                            {/* Month Navigation */}
                            <Box
                                sx={{
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "space-between",
                                    mb: 1.5,
                                }}
                            >
                                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                    <IconButton
                                        size="small"
                                        onClick={handlePreviousMonth}
                                        disabled={!canGoPrevious}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            color: "primary.main",
                                            "&:hover": {
                                                bgcolor: "action.hover",
                                            },
                                            "&:disabled": {
                                                opacity: 0.3,
                                            },
                                        }}
                                    >
                                        <ChevronLeft fontSize="small" />
                                    </IconButton>
                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontSize: "0.813rem",
                                            fontWeight: 600,
                                            color: "#374151",
                                            minWidth: 140,
                                            textAlign: "center",
                                        }}
                                    >
                                        {getDisplayTitle()}
                                    </Typography>
                                    <IconButton
                                        size="small"
                                        onClick={handleNextMonth}
                                        disabled={!canGoNext}
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            color: "primary.main",
                                            "&:hover": {
                                                bgcolor: "action.hover",
                                            },
                                            "&:disabled": {
                                                opacity: 0.3,
                                            },
                                        }}
                                    >
                                        <ChevronRight fontSize="small" />
                                    </IconButton>
                                </Box>
                            </Box>
                            {isLoadingProgress ? (
                                <Box sx={{ display: "flex", justifyContent: "center", py: 2, width: "200px" }}>
                                    <CircularProgress size="18px" />
                                </Box>
                            )
                                : (
                                    <Box
                                        sx={{
                                            display: "flex",
                                            flexWrap: "wrap",
                                            gap: "10px 8px", // row-gap column-gap (vertical gap between rows, horizontal gap between cells)
                                            maxWidth: "calc(18px * 10 + 8px * 9)", // Max 10 cells per row
                                        }}
                                    >
                                        {progressData?.map((day, index) => (
                                            <Tooltip
                                                key={index}
                                                title={
                                                    <Box>
                                                        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                                                            {day.date.toFormat('MMM d, yyyy')}
                                                        </Typography>
                                                        <Typography variant="caption">
                                                            Status: {getStatusLabel(day.status)}
                                                        </Typography>
                                                    </Box>
                                                }
                                                arrow
                                                placement="top"
                                                componentsProps={{
                                                    tooltip: {
                                                        sx: {
                                                            bgcolor: 'rgba(0, 0, 0, 0.85)',
                                                            fontSize: '0.75rem',
                                                            padding: '8px 12px',
                                                            borderRadius: '6px',
                                                            '& .MuiTooltip-arrow': {
                                                                color: 'rgba(0, 0, 0, 0.85)',
                                                            },
                                                        },
                                                    },
                                                }}
                                            >
                                                <Box
                                                    sx={{
                                                        width: 18,
                                                        height: 18,
                                                        bgcolor: getStatusChartColor(day.status),
                                                        borderRadius: "3px",
                                                        cursor: "pointer",
                                                        transition: "all 0.2s cubic-bezier(0.4, 0, 0.2, 1)",
                                                        position: "relative",
                                                        "&:hover": {
                                                            transform: "scale(1.25)",
                                                            zIndex: 1,
                                                            boxShadow: "0 4px 8px rgba(0, 0, 0, 0.15)",
                                                            border: "2px solid rgba(0, 0, 0, 0.2)",
                                                        },
                                                    }}
                                                />
                                            </Tooltip>
                                        ))}
                                    </Box>
                                )}
                        </Box>

                        {/* Right: Legend */}

                        <Box
                            sx={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 0.75,
                                minWidth: 120,
                                pt: 2.5,
                            }}
                        >
                            <Typography
                                variant="caption"
                                sx={{
                                    fontSize: "0.688rem",
                                    fontWeight: 600,
                                    color: "#6b7280",
                                    mb: 0.25,
                                }}
                            >
                                Status
                            </Typography>
                            <Box sx={{ display: "flex", flexDirection: "column", gap: 0.75 }}>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            bgcolor: "#ebedf0",
                                            borderRadius: "2px",
                                        }}
                                    />
                                    <Typography variant="caption" sx={{ fontSize: "0.688rem", color: "#6b7280" }}>
                                        Not Started
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            bgcolor: "#ffd89b",
                                            borderRadius: "2px",
                                        }}
                                    />
                                    <Typography variant="caption" sx={{ fontSize: "0.688rem", color: "#6b7280" }}>
                                        In Progress
                                    </Typography>
                                </Box>
                                <Box sx={{ display: "flex", alignItems: "center", gap: 0.75 }}>
                                    <Box
                                        sx={{
                                            width: 10,
                                            height: 10,
                                            bgcolor: "#40c463",
                                            borderRadius: "2px",
                                        }}
                                    />
                                    <Typography variant="caption" sx={{ fontSize: "0.688rem", color: "#6b7280" }}>
                                        Completed
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>

                    </Box>
                </Box>
            )}
        </Box>
    );
};

export default GoalCard;
